/** @forge-header
 * @id          wnbaParsers
 * @role        WNBA ticker parsers | game moneyline + season props
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/teamNames:teamNameFromCode,splitWnbaTeams
 * @downstream  kalshiTicker/parse
 * @exports     parseWnbaGame:fn | parseWnbaSeason:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback } from '../helpers';
import { teamNameFromCode, splitWnbaTeams } from '../teamNames';

/**
 * WNBA game moneyline: KXWNBAGAME-26MAY13INDLA-LA
 *
 * Variable-length team codes (2-char LA/LV/NY/GS, 4-char CONN) — can't reuse
 * NBA's fixed 3+3 slice. Walk every split point of the team-pair chunk and
 * pick the one where both halves are canonical WNBA codes.
 */
export function parseWnbaGame(raw: string): ParsedTicker {
  const parts = raw.includes('--') ? raw.split('--') : raw.split('-');

  let eventPart: string;
  let teamCode: string | null = null;

  if (raw.includes('--')) {
    eventPart = parts[0] ?? '';
    teamCode = parts[1] ?? null;
  } else {
    teamCode = parts[parts.length - 1] ?? null;
    eventPart = parts.slice(0, -1).join('-');
  }

  const eventParts = eventPart.split('-');
  const mid = eventParts[1] ?? '';

  // Date is 7 chars; codes are at least 2+2 = 4 → mid is at least 11 chars.
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);
  const split = splitWnbaTeams(teamCodes);

  if (!split) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = split;

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: teamNameFromCode(awayCode, 'wnba'),
    homeName: teamNameFromCode(homeCode, 'wnba'),
  };

  const shortLabel = `WNBA: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `WNBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${teamCode} Win`
    : `WNBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'wnba-game',
    series: 'KXWNBAGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

/** WNBA season props: KXWNBA-26-LV */
export function parseWnbaSeason(raw: string): ParsedTicker {
  const parts = raw.split('-');
  const year = parts[1] ? `20${parts[1]}` : null;
  const outcome = parts[2] ?? null;

  const shortLabel = outcome ? `WNBA Season: ${outcome}` : 'WNBA Season Prop';
  const fullLabel = year ? `WNBA ${year} Season${outcome ? ` · ${outcome}` : ''}` : shortLabel;

  return {
    raw,
    category: 'wnba-season',
    series: 'KXWNBA',
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
